import { useCallback, useState } from 'react';
import { type OAuthConsentProvider } from '@/features/oauth/components/OAuthConsentModal';

/**
 * OAuth consent interstitial gate (#2703 follow-up).
 *
 * Verbatim cluster move out of ChatBox.tsx: the `pendingOAuthConsent` state and
 * the `confirmOAuthConsent` callback that resolves once the user confirms/declines
 * in the OAuthConsentModal. Shown BEFORE connectProvider redirects to Google's
 * consent screen, so the user sees exactly what Xavior will and won't do
 * (read-only, no send) without having to read the Google scope list.
 *
 * Hooks are preserved in their original order and with byte-identical dependency
 * arrays so React's rules of hooks and effect ordering remain unchanged. The
 * <OAuthConsentModal> JSX stays in ChatBox and consumes the returned
 * `pendingOAuthConsent` / `setPendingOAuthConsent`.
 */
export function useOAuthConsentGate() {
  const [pendingOAuthConsent, setPendingOAuthConsent] = useState<{
    provider: OAuthConsentProvider;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const confirmOAuthConsent = useCallback(
    (provider: OAuthConsentProvider): Promise<boolean> =>
      new Promise((resolve) => setPendingOAuthConsent({ provider, resolve })),
    [],
  );

  return { pendingOAuthConsent, setPendingOAuthConsent, confirmOAuthConsent };
}
